import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import MaskedInput, {MaskedInputProps} from 'react-text-mask';
import {createNumberMask} from 'text-mask-addons';

import {
  CURRENCY_MASK_OPTIONS,
  DATE_OF_BIRTH_MASK,
  NUMBER_MASK_OPTIONS,
  PHONE_MASK,
  SOCIAL_SECURITY_NUMBER_MASK,
  ZIP_MASK,
} from '../../../constants';
import LabelStyles from '../../../shared/label-style';
import {useOnChange} from '../../../utils/renderUtils';

import {
  COMP_NAME,
  Container,
  InputWrapper,
  Loader,
  StyledInput,
} from './styles';
import {InputMode, InputProps, InputTypeProp} from './types';
import {inputValidator, numInputValidator, stringTypes} from './utils';

const MAX_LENGTH_BY_TYPE = {
  currency: 12,
  number: 9,
} as Record<InputTypeProp, number>;

const Input: FC<InputProps> = ({
  value,
  label,
  testId,
  type = 'string',
  required,
  optional,
  loading,
  onChange,
  hintText,
  hasError,
  onValidationChange,
  name,
  onKeyDown,
  onKeyUp,
  validator,
  uppercaseFirst,
  style,
  errorMessage,
  iconSrc,
  placeholder,
  onClickIcon,
  iconPosition,
  fitParent,
  labelStyle,
  wrapperStyle,
  inputStyle,
  disabled,
  className,
  id,
  size = 'normal',
  textAlign = 'left',
  innerRef,
  max,
  min,
  isReady,
  customNumberMask,
  autoComplete,
}) => {
  const [touched, setTouched] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [typeState, setTypeState] = useState(type);

  const inputRef = useRef(null);

  // Synchronous validity is a pure function of the value and its constraints.
  const syncValid = useMemo(() => {
    const valid = inputValidator(typeState, value, required);

    if (valid && typeState === 'number') {
      return numInputValidator({value: +value, min: +min, max: +max});
    }

    return valid;
  }, [typeState, value, required, min, max]);

  /*
   * An optional async `validator` can overturn the synchronous verdict. Its
   * answer is held against the value it was asked about, so a newer value falls
   * back to the synchronous result until that value's own answer arrives.
   */
  const [asyncVerdict, setAsyncVerdict] = useState<{
    base: string;
    valid: boolean;
  }>();
  const isValid =
    validator && asyncVerdict && asyncVerdict.base === value
      ? asyncVerdict.valid
      : syncValid;

  const innerHasError = touched && !isValid;
  const hasErrorStatus = hasError || innerHasError;

  /*
   * effect:audited — awaits the caller's async validator, which cannot happen
   * during render. The verdict lands through a promise callback rather than
   * synchronously, and is discarded if the value moved on first.
   *
   * An empty value is synchronously valid on purpose (nothing typed yet is not
   * an error), so `syncValid` alone fires the validator on mount — which is how
   * the onboarding zip field sent `zip_tz?zip_code=undefined` on every render of
   * the step. There is nothing to ask a remote validator about until there is a
   * value; `value?.length` is the same emptiness test `inputValidator` applies.
   */
  useEffect(() => {
    if (!validator || !syncValid || !value?.length) return undefined;

    let cancelled = false;

    Promise.resolve(validator)
      .then((res) => res(value, syncValid))
      .then((valid) => {
        if (!cancelled) setAsyncVerdict({base: value, valid});
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [validator, value, syncValid]);

  // Tell the parent when validity flips. Validity also moves when the caller
  // changes `value` or the constraints, so this cannot live in a change handler.
  useOnChange(isValid, () => onValidationChange?.(isValid, name));

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = event.target.value;

    setTouched(true);

    // Remove empty spaces
    if (
      typeState === 'email' ||
      typeState === 'mobilePhone' ||
      typeState === 'zip' ||
      typeState === 'url'
    ) {
      newValue = newValue.trim();
    }

    // Remove mask characters
    if (
      typeState !== 'string' &&
      typeState !== 'stringNoSymbols' &&
      typeState !== 'stringNoSymbolsNoNumbers' &&
      typeState !== 'address' &&
      typeState !== 'email'
    ) {
      newValue = newValue.replace(/-|_|\(|\)| |\$|,/g, '');
    }

    if (uppercaseFirst) {
      newValue = newValue
        .split(' ')
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join(' ');
    }

    onChange?.(newValue, event.target);
  };

  const wrapperKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (typeState === 'number' && event.nativeEvent?.code?.includes?.('Key')) {
      event.preventDefault();
    }

    if (event.nativeEvent.code !== 'Enter') {
      return;
    }

    onKeyDown?.(event.nativeEvent.code, event);
  };

  const wrapperKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.nativeEvent.code !== 'Enter' &&
      event.nativeEvent.code !== 'Comma' &&
      event.nativeEvent.code !== 'Space'
    ) {
      return;
    }

    onKeyUp?.(event.nativeEvent.code, event);
  };

  let containerStyle: React.CSSProperties = fitParent ? {width: '100%'} : {};

  if (style) {
    containerStyle = {...containerStyle, ...style};
  }

  let inputMode: InputMode = null;

  if (typeState === 'email' || typeState === 'url') inputMode = typeState;

  const numericStates = [
    'zip',
    'mobilePhone',
    'currency',
    'noSymbolCurrency',
    'dateOfBirth',
    'socialSecurityNumber',
  ];

  if (numericStates.includes(typeState)) inputMode = 'numeric';

  let inputType: string = typeState;

  if (stringTypes.includes(typeState)) {
    inputType = 'text';
  }

  let wrapperClassName = '';

  if (loading) wrapperClassName += ` ${COMP_NAME}--loading`;

  if (touched && (!isValid || hasError)) {
    wrapperClassName += ` ${COMP_NAME}--invalid ${hasError ? 'zactive' : ''}`;
  }

  const inputProps: Partial<MaskedInputProps> = {
    id,
    ...(value !== undefined && {value}),
    type: inputType,
    name,
    ...(MAX_LENGTH_BY_TYPE[typeState]
      ? {maxLength: MAX_LENGTH_BY_TYPE[typeState]}
      : {}),
    placeholder,
    required,
    disabled: loading || disabled,
    inputMode,
    min,
    max,
    onChange: onInputChange,
    onKeyDown: wrapperKeyDown,
    onKeyUp: wrapperKeyUp,
  };

  let maskElement: Array<string | RegExp> = [];

  if (typeState === 'mobilePhone') maskElement = PHONE_MASK;
  else if (typeState === 'zip') maskElement = ZIP_MASK;
  else if (typeState === 'dateOfBirth') maskElement = DATE_OF_BIRTH_MASK;
  else if (typeState === 'socialSecurityNumber')
    maskElement = SOCIAL_SECURITY_NUMBER_MASK;
  else if (typeState === 'currency')
    maskElement = createNumberMask({
      ...CURRENCY_MASK_OPTIONS,
      ...customNumberMask,
    });
  else if (typeState === 'number')
    maskElement = createNumberMask({
      ...NUMBER_MASK_OPTIONS,
      ...customNumberMask,
    });
  else if (typeState === 'noSymbolCurrency')
    maskElement = createNumberMask({
      ...CURRENCY_MASK_OPTIONS,
      ...customNumberMask,
      prefix: '',
    });

  const finalInput: React.ReactElement =
    maskElement.length || typeof maskElement === 'function' ? (
      <MaskedInput
        {...inputProps}
        guide
        ref={(instance) => {
          if (innerRef) innerRef.current = instance;
        }}
        mask={maskElement}
        autoComplete={autoComplete}
        onMouseDown={() => {
          setClicked(true);
        }}
        onBlur={() => {
          setClicked(false);
        }}
        render={(ref, renderProps) => {
          return (
            <StyledInput
              ref={(input) => ref(input)}
              style={inputStyle}
              data-testid={testId}
              {...renderProps}
            />
          );
        }}
      />
    ) : (
      <StyledInput
        {...inputProps}
        ref={(instance) => {
          if (innerRef) innerRef.current = instance;
        }}
        data-testid={testId}
        style={inputStyle}
        autoComplete={autoComplete}
        onMouseDown={() => {
          setClicked(true);
        }}
        onBlur={() => {
          setClicked(false);
        }}
      />
    );

  const showPassword = () => {
    if (typeState === 'password') {
      setTypeState('string');
    } else {
      setTypeState('password');
    }
  };

  const handleFocusStyle = () => {
    if (hasErrorStatus) {
      return 'var(--border-error)';
    }

    if (clicked) {
      return 'var(--surface-brand)';
    }

    return 'var(--text-default)';
  };

  return (
    <Container
      $size={size}
      $fitParent={fitParent}
      style={containerStyle}
      $colorOnFocus={handleFocusStyle()}
      $disabled={disabled}>
      {label?.length ? (
        <LabelStyles style={labelStyle} htmlFor={id}>
          {label}
        </LabelStyles>
      ) : (
        <LabelStyles
          style={labelStyle}
          htmlFor={id}
          className="visually-hidden">
          {type} {required && 'required'}
        </LabelStyles>
      )}

      <InputWrapper
        $inputType={type}
        $size={size}
        $textAlign={textAlign}
        $hasIcon={!!iconSrc}
        $hasError={hasErrorStatus}
        ref={inputRef}
        $iconPosition={iconPosition || 'left'}
        $colorOnFocus={handleFocusStyle()}
        style={wrapperStyle}
        $disabled={disabled}
        className={`${wrapperClassName} ${className || ''}`}>
        {finalInput}

        {/*
          A button only when there is something to click. Without a handler the
          icon is decoration, and rendering it as a button would put an empty
          control in the tab order.
        */}
        {!!iconSrc && !!onClickIcon && (
          <button
            type="button"
            aria-label="Input action"
            className={`${COMP_NAME}__icon-search`}
            onClick={onClickIcon}>
            <img src={iconSrc} alt="" />
          </button>
        )}

        {!!iconSrc && !onClickIcon && (
          <span className={`${COMP_NAME}__icon-search`}>
            <img src={iconSrc} alt="input icon" />
          </span>
        )}

        {loading && (
          <div className={`${COMP_NAME}__loader`}>
            <Loader className="gray" />
          </div>
        )}

        {isReady && (
          <span className={`${COMP_NAME}__is-ready`}>
            <i className="fa-light fa-circle-check" aria-hidden />
          </span>
        )}

        {type === 'password' && (
          <button
            type="button"
            className={`${COMP_NAME}__show_password`}
            onClick={showPassword}>
            {typeState === 'password' ? 'SHOW' : 'HIDE'}
          </button>
        )}
      </InputWrapper>

      {(hasError || hintText || optional) && (
        <div className={`${COMP_NAME}__hint-container`}>
          {!!errorMessage && (hasError || !isValid) && (
            <div className={`${COMP_NAME}__hint-container_error`}>
              {errorMessage}
            </div>
          )}

          {!hasError && hintText && (
            <div className={`${COMP_NAME}__hint-container_hint`}>
              {hintText}
            </div>
          )}

          {optional && (
            <div className={`${COMP_NAME}__hint-container_optional-message`}>
              Optional
            </div>
          )}
        </div>
      )}
    </Container>
  );
};

export default Input;
